import { activityJobTypesSharePhase } from './identity';
import { stableActivityBuildingPublicId } from './job';
import type { ActivityItem } from './models';

export function reconcileSharePointPlaceholderProvenance(
    items: readonly ActivityItem[]
): readonly ActivityItem[] {
    return propagateObservedIntake(
        retireSupersededSharePointPlaceholders(items)
    );
}

function retireSupersededSharePointPlaceholders(
    items: readonly ActivityItem[]
): readonly ActivityItem[] {
    const superseded = items.flatMap((item) => {
        const placeholder = item.jobs.length === 1 ? item.jobs[0] : null;
        if (
            !placeholder ||
            item.correlationId === null ||
            item.availability !== 'available' ||
            !isQueuedZeroSharePointPlaceholder(placeholder) ||
            !placeholder.buildingPublicId
        ) {
            return [];
        }

        const hasDownstream = items.some(
            (downstream) =>
                downstream !== item &&
                downstream.correlationId === item.correlationId &&
                downstream.availability === 'available' &&
                stableActivityBuildingPublicId(downstream.jobs) ===
                    placeholder.buildingPublicId &&
                downstream.jobs.some(
                    (job) =>
                        isMeaningfulDownstreamHandoff(job) &&
                        Date.parse(job.occurredAtUtc) >
                            Date.parse(placeholder.occurredAtUtc)
                )
        );
        return hasDownstream ? [{ item, placeholder }] : [];
    });

    if (superseded.length === 0) return items;

    return items
        .filter(
            (item) =>
                !superseded.some(
                    ({ item: placeholderItem }) => placeholderItem === item
                )
        )
        .map((item) => {
            const source = superseded.find(
                ({ item: placeholderItem, placeholder }) =>
                    item.correlationId === placeholderItem.correlationId &&
                    stableActivityBuildingPublicId(item.jobs) ===
                        placeholder.buildingPublicId
            )?.placeholder;
            return source
                ? {
                      ...item,
                      observedIntake: {
                          jobType: source.jobType as 'SharePointImport',
                          message: source.message,
                          occurredAtUtc: source.occurredAtUtc,
                      },
                  }
                : item;
        });
}

function propagateObservedIntake(
    items: readonly ActivityItem[]
): readonly ActivityItem[] {
    return items.map((item) => {
        if (item.observedIntake || item.correlationId === null) return item;

        const buildingPublicId = stableActivityBuildingPublicId(item.jobs);
        const lineageSource = items.find(
            (candidate) =>
                candidate.observedIntake &&
                candidate.correlationId === item.correlationId &&
                stableActivityBuildingPublicId(candidate.jobs) ===
                    buildingPublicId
        )?.observedIntake;

        return lineageSource
            ? { ...item, observedIntake: lineageSource }
            : item;
    });
}

function isQueuedZeroSharePointPlaceholder(
    job: ActivityItem['jobs'][number]
): boolean {
    return (
        job.jobType === 'SharePointImport' &&
        job.status === 'Queued' &&
        job.percentComplete === 0 &&
        !job.warning
    );
}

function isMeaningfulDownstreamHandoff(
    update: ActivityItem['jobs'][number]
): boolean {
    return (
        (activityJobTypesSharePhase(update.jobType, 'DocumentClassification') ||
            activityJobTypesSharePhase(
                update.jobType,
                'DocumentIntelligence'
            )) &&
        update.status !== 'Queued' &&
        update.status !== 'Skipped' &&
        update.status !== 'Expired'
    );
}
